import { Component, ChangeDetectorRef, OnInit, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { finalize } from 'rxjs';
import { TsSnackbarService } from 'tsui';
import { TreoValidators } from 'tsui/@treo';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import * as moment from 'moment-timezone';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { User } from 'app/core/models/user.types';
import { UserService } from 'app/layout/common/user/user.service';
import { Account, AccountIntegration } from 'app/core/models/account.types';
import { AccountIntegrationSlug } from 'app/shared/enums';
import { environment } from '@env/environment';
import * as CustomValidator from 'app/shared/directives/custom-validators.directive';
import { QUILL_MODULES } from 'app/shared/Utils/quill-editor.utils';

@Component({
  selector: 'general-profile',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GeneralProfileComponent extends OnDestroyMixin implements OnInit {
  userForm: UntypedFormGroup;
  resetPasswordForm: UntypedFormGroup;
  signatureForm: UntypedFormGroup;
  quillModules = QUILL_MODULES;
  timezoneOptions: string[] = moment.tz.zonesForCountry('US');

  public user: User;
  public users: User[];
  public account: Account;
  public accountIntegrations: AccountIntegration[] = [];

  @ViewChild('avatarFileInput', { static: true }) private _avatarFileInput: ElementRef;

  constructor(
    private readonly _userService: UserService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly _snackBar: TsSnackbarService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _accountService: AccountService,
    private readonly store: Store,
  ) {
    super();

    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  ngOnInit(): void {
    this._accountService.users$.pipe(untilComponentDestroyed(this)).subscribe((users) => {
      this.users = users;
      this.buildForm();
      this.getUser();
      this.getUserSignature();
      this.loadIntegrations();
    });
  }

  private buildForm(): void {
    this.userForm = this.formBuilder.group({
      id: [''],
      full_name: [''],
      time_zone: [null],
      avatar_url: [null],
    });

    this.signatureForm = this.formBuilder.group({
      body: [''],
    });

    this.resetPasswordForm = this.formBuilder.group(
      {
        id: [''],
        password: ['', Validators.required],
        confirm_password: ['', Validators.required],
      },
      {
        validators: TreoValidators.mustMatch('password', 'confirm_password'),
      },
    );
  }

  private getUser(): void {
    this._userService.user$.pipe(untilComponentDestroyed(this)).subscribe((user: User) => {
      this.user = user;
      this.userForm.patchValue(this.user);
      this.resetPasswordForm.patchValue(this.user);
      this._changeDetectorRef.markForCheck();
    });
  }

  private getUserSignature(): void {
    this._userService
      .getUserSignature()
      .pipe(untilComponentDestroyed(this))
      .subscribe((signature) => {
        this.signatureForm.patchValue({
          body: signature,
        });
      });
  }

  private refreshUsers(): void {
    this._accountService.bustUsersCache();
    this._accountService
      .getUsers()
      .pipe(untilComponentDestroyed(this))
      .subscribe((users) => {
        this.users = users;
        this._changeDetectorRef.markForCheck();
      });
  }

  private loadIntegrations(): void {
    this._accountService
      .getAccountIntegrations()
      .pipe(untilComponentDestroyed(this))
      .subscribe((account_integrations) => {
        if (account_integrations) {
          this.accountIntegrations = account_integrations.filter(
            (ai) => ai.integration.slug === AccountIntegrationSlug.Azure,
          );
        }
        this._changeDetectorRef.markForCheck();
      });
  }

  onNameFieldChange(fieldName: string): void {
    const field = this.userForm.get(fieldName);
    if (field && field.dirty) {
      field.setValidators([CustomValidator.uniqueNameValidator(this.users, fieldName, true)]);
      field.updateValueAndValidity();
    }
    if (field.value === this.user.full_name) {
      field.clearValidators();
      field.updateValueAndValidity();
    }
  }

  connectIntegration(accountIntegration: AccountIntegration): void {
    window.open(
      // eslint-disable-next-line max-len
      `${environment.baseUrl}/integrations/${accountIntegration.integration.slug}/setup?integration_id=${accountIntegration.integration_id}&user_id=${this.user.id}`,
    );
  }

  saveSignature(): void {
    this.signatureForm.disable();
    this._userService
      .saveUserSignature(this.signatureForm.value)
      .pipe(untilComponentDestroyed(this))
      .subscribe((signature) => {
        this.signatureForm.patchValue({
          body: signature,
        });
        this.signatureForm.enable();
        this._snackBar.open('Email signature updated successfully.', 'success');
        this._changeDetectorRef.markForCheck();
      });
  }

  updateProfile(): void {
    if (this.userForm.invalid) {
      return;
    }

    this.userForm.disable();

    const updatedUser = this.userForm.value;

    this._userService
      .update(updatedUser)
      .pipe(
        untilComponentDestroyed(this),
        finalize(() => {
          this.refreshUsers();
        }),
      )
      .subscribe({
        next: (user) => {
          // Reset the form with the updated settings and reset validators
          this.userForm.enable();
          this.userForm.reset(user);
          this.userForm.get('full_name').clearValidators();
          this.userForm.get('full_name').updateValueAndValidity();
          this._snackBar.open('Profile updated successfully.', 'success');
        },
        error: () => {
          this.userForm.enable();
          this._snackBar.open('Unable to update profile at the moment.', 'error');
        },
      });
  }

  updatePassword(): void {
    // Do nothing if the form is invalid
    if (this.resetPasswordForm.invalid) {
      return;
    }

    // Disable the form
    this.resetPasswordForm.disable();

    // Do your action here...
    const credentials = this.resetPasswordForm.value;

    // Sign in
    this._userService
      .update(credentials)
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: () => {
          this.resetPasswordForm.enable();
          this._snackBar.open('Password updated successfully.', 'success');
        },
        error: () => {
          // Re-enable the form
          this.resetPasswordForm.enable();
          // Show the error message
          this._snackBar.open('Unable to update password at the moment.', 'error');
        },
      });
  }

  uploadAvatar(fileList: FileList): void {
    // Return if canceled
    if (!fileList.length) return;

    const allowedTypes = ['image/jpeg', 'image/png'];
    const file: File = fileList[0];

    // Return if the file is not allowed
    if (!allowedTypes.includes(file.type)) return;

    // Upload the avatar
    this._userService
      .uploadProfilePhoto(file)
      .pipe(untilComponentDestroyed(this))
      .subscribe((user) => {
        this.user = user;
        this._snackBar.open('Profile photo updated successfully.', 'success');
      });
  }

  removeAvatar(): void {
    const avatarFormControl = this.userForm.get('avatar_url');
    avatarFormControl.setValue(null);
    this._avatarFileInput.nativeElement.value = null;
    this.user.avatar_url = null;

    this._userService
      .deleteProfilePhoto()
      .pipe(untilComponentDestroyed(this))
      .subscribe((user) => {
        this.user = user;
        this._snackBar.open('Profile photo removed successfully.', 'success');
      });
  }
}
